import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getToken, base_url } from "../../config/config";

const ProjectDetails = () => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [updatedCost, setUpdatedCost] = useState({});
  const [isNewCost, setIsNewCost] = useState(false);
  const [companies, setCompanies] = useState([]);
  const { anr } = useParams();
  const token = getToken();

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getProjectbyANR`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ANR: anr }),
        });
        const result = await response.json();
        if (response.ok) {
          setProject(result.data[0]);
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [anr]);

  // Fetch company list
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getFirma`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (response.ok) {
          setCompanies(result.data);
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        setError(error.message);
      }
    };

    fetchCompanies();
  }, []);

  const handleEdit = (materialCostId) => {
    setEditMode(materialCostId);
    const materialCost = project.materialCosts.find(
      (cost) => cost._id === materialCostId,
    );
    setUpdatedCost(materialCost);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Check if the change is for a nested field
    if (name.startsWith("ExtraCost.")) {
      const field = name.split(".")[1];
      setUpdatedCost((prevCost) => {
        const newExtraCost = { ...prevCost.ExtraCost, [field]: value };

        // Sum all extra costs
        const extraCostsTotal = Object.values(newExtraCost).reduce(
          (total, cost) => total + Number(cost || 0),
          0,
        );

        // Calculate TotalMaterialCost with units, singleCost, and extra costs
        const newTotalMaterialCost = (
          Number(prevCost.units) * Number(prevCost.singleCost) +
          extraCostsTotal
        ).toFixed(2);

        return {
          ...prevCost,
          ExtraCost: newExtraCost,
          TotalMaterialCost: newTotalMaterialCost,
        };
      });
    } else {
      setUpdatedCost((prevCost) => {
        const newCost = { ...prevCost, [name]: value };

        // Calculate TotalMaterialCost only if it is not manually set
        if (name === "units" || name === "singleCost") {
          const extraCostsTotal = Object.values(newCost.ExtraCost).reduce(
            (total, cost) => total + Number(cost || 0),
            0,
          );

          if (!updatedCost.TotalMaterialCostEditable) {
            newCost.TotalMaterialCost = (
              Number(newCost.units) * Number(newCost.singleCost) +
              extraCostsTotal
            ).toFixed(2);
          }
        }

        return newCost;
      });
    }
  };

  const handleSave = async () => {
    try {
      if (isNewCost) {
        // Adding a new material cost
        const updatedMaterialCosts = [...project.materialCosts, updatedCost];
        const response = await fetch(`${base_url}/v1/updateProject`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ANR: anr,
            materialCosts: updatedMaterialCosts,
          }),
        });

        const result = await response.json();
        if (response.ok) {
          setProject(result.data);
          setIsNewCost(false);
          setUpdatedCost({});
        } else {
          alert("Failed to add new material cost");
        }
      } else {
        // Updating an existing material cost
        const response = await fetch(`${base_url}/v1/updateMaterialCost`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ANR: anr,
            materialCostId: editMode,
            updatedMaterialCost: updatedCost,
          }),
        });

        const result = await response.json();
        if (response.ok) {
          setProject(result.data);
          setEditMode(null);
          setUpdatedCost({});
        } else {
          alert("Failed to update material cost");
        }
      }
    } catch (err) {
      alert("Error saving material cost");
    }
  };

  const handleDelete = async (materialCostId) => {
    try {
      const response = await fetch(`${base_url}/v1/deleteMaterialCost`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ANR: anr, materialCostId }),
      });

      const result = await response.json();
      if (response.ok) {
        setProject(result.data);
      } else {
        alert("Failed to delete material cost");
      }
    } catch (err) {
      alert("Error deleting material cost");
    }
  };

  const handleAddNew = () => {
    setIsNewCost(true);
    setEditMode(null);
    setUpdatedCost({
      name: "",
      companyName: "",
      description: "",
      unitName: "",
      units: "",
      singleCost: "",
      TotalMaterialCost: "",
      TotalMaterialCostEditable: false, // Indicates if the TotalMaterialCost is editable
      InvoiceNo: "",
      Artikelnummer: "",
      ExtraCost: {
        werk: "0",
        transport: "0",
        zuschnitte: "0",
        positionsKosten: "0",
        Zeugniskosten: "0",
      },
    });
  };

  const handleCancelAddNew = () => {
    setIsNewCost(false);
    setUpdatedCost({});
  };

  if (loading) return <p style={styles.loading}>Loading...</p>;
  if (error) return <p style={styles.error}>Error: {error}</p>;

  return (
    <div style={styles.container}>
      {project ? (
        <div style={styles.projectDetails}>
          <h2 style={styles.title}>Project Details for ANR: {project.ANR}</h2>
          <p style={styles.text}>
            <strong>Status:</strong> {project.status}
          </p>
          <p style={styles.text}>
            <strong>Created At:</strong>{" "}
            {new Date(project.createdAt).toLocaleDateString()}
          </p>
          <p style={styles.text}>
            <strong>Updated At:</strong>{" "}
            {new Date(project.updatedAt).toLocaleDateString()}
          </p>

          <h3 style={styles.subtitle}>Material Costs</h3>
          <ul style={styles.materialList}>
            {project.materialCosts.map((material) => (
              <li key={material._id} style={styles.materialItem}>
                {editMode === material._id ? (
                  <div style={styles.formGroup}>
                    <input
                      type='text'
                      name='name'
                      value={updatedCost.name || ""}
                      onChange={handleChange}
                      placeholder='Name'
                      style={styles.input}
                    />
                    <select
                      name='companyName'
                      value={updatedCost.companyName || ""}
                      onChange={handleChange}
                      style={styles.input}>
                      <option value=''>Select Company</option>
                      {companies.map((company) => (
                        <option key={company._id} value={company.bezeichnung}>
                          {company.bezeichnung}
                        </option>
                      ))}
                    </select>
                    <input
                      type='text'
                      name='description'
                      value={updatedCost.description || ""}
                      onChange={handleChange}
                      placeholder='Description'
                      style={styles.input}
                    />
                    <input
                      type='text'
                      name='unitName'
                      value={updatedCost.unitName || ""}
                      onChange={handleChange}
                      placeholder='Unit Name'
                      style={styles.input}
                    />

                    <input
                      type='number'
                      name='units'
                      value={updatedCost.units || ""}
                      onChange={handleChange}
                      placeholder='Units'
                      style={styles.input}
                    />
                    <input
                      type='number'
                      name='singleCost'
                      value={updatedCost.singleCost || ""}
                      onChange={handleChange}
                      placeholder='Single Cost'
                      style={styles.input}
                    />
                    <input
                      type='number'
                      name='TotalMaterialCost'
                      value={updatedCost.TotalMaterialCost || ""}
                      onChange={handleChange}
                      placeholder='Total Material Cost'
                      style={styles.input}
                      onFocus={() =>
                        setUpdatedCost((prev) => ({
                          ...prev,
                          TotalMaterialCostEditable: true,
                        }))
                      }
                      onBlur={() =>
                        setUpdatedCost((prev) => ({
                          ...prev,
                          TotalMaterialCostEditable: false,
                        }))
                      }
                    />
                    <input
                      type='text'
                      name='InvoiceNo'
                      value={updatedCost.InvoiceNo || ""}
                      onChange={handleChange}
                      placeholder='Invoice No'
                      style={styles.input}
                    />
                    <input
                      type='text'
                      name='Artikelnummer'
                      value={updatedCost.Artikelnummer || ""}
                      onChange={handleChange}
                      placeholder='Artikelnummer'
                      style={styles.input}
                    />
                    <div style={styles.extraCosts}>
                      {Object.entries(updatedCost.ExtraCost).map(
                        ([key, value]) => (
                          <input
                            key={key}
                            type='text'
                            name={`ExtraCost.${key}`}
                            value={value || ""}
                            onChange={handleChange}
                            placeholder={
                              key.charAt(0).toUpperCase() + key.slice(1)
                            }
                            style={styles.input}
                          />
                        ),
                      )}
                    </div>
                    <button onClick={handleSave} style={styles.saveButton}>
                      Save
                    </button>
                    <button
                      onClick={() => setEditMode(null)}
                      style={styles.cancelButton}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div>
                    <p>
                      <strong>Name:</strong> {material.name}
                    </p>
                    <p>
                      <strong>Company:</strong> {material.companyName}
                    </p>
                    <p>
                      <strong>Description:</strong> {material.description}
                    </p>
                    <p>
                      <strong>Unit Name:</strong> {material.unitName}
                    </p>
                    <p>
                      <strong>Units:</strong> {material.units}
                    </p>
                    <p>
                      <strong>Single Cost:</strong> {material.singleCost}
                    </p>
                    <p>
                      <strong>Total Material Cost:</strong>{" "}
                      {material.TotalMaterialCost}
                    </p>
                    <p>
                      <strong>Invoice No:</strong> {material.InvoiceNo}
                    </p>
                    <p>
                      <strong>Artikelnummer:</strong> {material.Artikelnummer}
                    </p>
                    <p>
                      <strong>Extra Costs:</strong>
                      {material.ExtraCost &&
                      Object.entries(material.ExtraCost).length > 0 ? (
                        Object.entries(material.ExtraCost).map(
                          ([key, value]) => (
                            <span key={key}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}:{" "}
                              {value},{" "}
                            </span>
                          ),
                        )
                      ) : (
                        <span>No Extra Costs</span>
                      )}
                    </p>

                    <button
                      onClick={() => handleEdit(material._id)}
                      style={styles.editButton}>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(material._id)}
                      style={styles.deleteButton}>
                      Delete
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {isNewCost ? (
            <div style={styles.formGroup}>
              <input
                type='text'
                name='name'
                value={updatedCost.name || ""}
                onChange={handleChange}
                placeholder='Name'
                style={styles.input}
              />
              <select
                name='companyName'
                value={updatedCost.companyName || ""}
                onChange={handleChange}
                style={styles.input}>
                <option value=''>Select Company</option>
                {companies.map((company) => (
                  <option key={company._id} value={company.bezeichnung}>
                    {company.bezeichnung}
                  </option>
                ))}
              </select>
              <input
                type='text'
                name='description'
                value={updatedCost.description || ""}
                onChange={handleChange}
                placeholder='Description'
                style={styles.input}
              />
              {/* <input
                type="text"
                name="unitName"
                value={updatedCost.unitName || ""}
                onChange={handleChange}
                placeholder="Unit Name"
                style={styles.input}
              /> */}

              <select
                name='unitName'
                value={updatedCost.unitName || ""}
                onChange={handleChange}
                style={styles.input}>
                <option value=''>Select Option</option>
                <option value='Stück'>Stück</option>
                <option value='m'>m</option>
                <option value='pschl'>pschl</option>
                <option value='kg'>kg</option>
              </select>
              <input
                type='number'
                name='units'
                value={updatedCost.units || ""}
                onChange={handleChange}
                placeholder='Units'
                style={styles.input}
              />
              <input
                type='number'
                name='singleCost'
                value={updatedCost.singleCost || ""}
                onChange={handleChange}
                placeholder='Single Cost'
                style={styles.input}
              />
              <input
                type='number'
                name='TotalMaterialCost'
                value={updatedCost.TotalMaterialCost || ""}
                onChange={handleChange}
                placeholder='Total Material Cost'
                style={styles.input}
                onFocus={() =>
                  setUpdatedCost((prev) => ({
                    ...prev,
                    TotalMaterialCostEditable: true,
                  }))
                }
                onBlur={() =>
                  setUpdatedCost((prev) => ({
                    ...prev,
                    TotalMaterialCostEditable: false,
                  }))
                }
              />
              <input
                type='text'
                name='InvoiceNo'
                value={updatedCost.InvoiceNo || ""}
                onChange={handleChange}
                placeholder='Invoice No'
                style={styles.input}
              />
              <input
                type='text'
                name='Artikelnummer'
                value={updatedCost.Artikelnummer || ""}
                onChange={handleChange}
                placeholder='Artikelnummer'
                style={styles.input}
              />
              <div style={styles.extraCosts}>
                <h3>EXTRA COST</h3>
                {Object.entries(updatedCost.ExtraCost).map(([key, value]) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}>
                    <label
                      style={{
                        fontSize: "20px",
                        marginRight: "10px",
                        flex: "1",
                      }}>
                      {key}
                    </label>
                    <input
                      key={key}
                      type='text'
                      name={`ExtraCost.${key}`}
                      value={value || ""}
                      onChange={handleChange}
                      placeholder={key.charAt(0).toUpperCase() + key.slice(1)}
                      style={{
                        flex: "2",
                        padding: "10px",
                        fontSize: "16px",
                        marginRight: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                ))}
              </div>
              <button onClick={handleSave} style={styles.saveButton}>
                Save
              </button>
              <button onClick={handleCancelAddNew} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={handleAddNew} style={styles.addNewButton}>
              Add New Material Cost
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    maxWidth: "800px",
    margin: "auto",
  },
  projectDetails: {
    border: "1px solid #ddd",
    padding: "20px",
    borderRadius: "4px",
  },
  title: {
    fontSize: "24px",
    marginBottom: "10px",
  },
  subtitle: {
    fontSize: "20px",
    marginBottom: "10px",
  },
  text: {
    fontSize: "16px",
    marginBottom: "5px",
  },
  materialList: {
    listStyleType: "none",
    padding: 0,
  },
  materialItem: {
    borderBottom: "1px solid #ddd",
    padding: "10px 0",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  input: {
    marginBottom: "10px",
    padding: "8px",
    fontSize: "16px",
  },
  saveButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    padding: "10px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  cancelButton: {
    backgroundColor: "#f44336",
    color: "white",
    padding: "10px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginLeft: "10px",
  },
  editButton: {
    backgroundColor: "#2196F3",
    color: "white",
    padding: "5px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#f44336",
    color: "white",
    padding: "5px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginLeft: "10px",
  },
  addNewButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    padding: "10px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  extraCosts: {
    marginBottom: "10px",
  },
  loading: {
    fontSize: "18px",
    textAlign: "center",
  },
  error: {
    color: "red",
    fontSize: "18px",
    textAlign: "center",
  },
};

export default ProjectDetails;
